import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATE_NAMES = { NC: "North Carolina", CT: "Connecticut" };
const stateFull = (s) => STATE_NAMES[(s || "").toUpperCase()] || s;

export default function WhereToBuy() {
  const [cities, setCities] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/retailers`);
        const data = await res.json();
        const groups = {};
        data.forEach((r) => {
          const key = `${(r.city || "").trim()}|${(r.state || "").trim().toUpperCase()}`;
          (groups[key] = groups[key] || []).push(r);
        });
        const list = Object.entries(groups)
          .map(([key, stores]) => {
            const [city, state] = key.split("|");
            return { city, state, stores: stores.sort((a, b) => a.name.localeCompare(b.name)) };
          })
          .sort((a, b) => b.stores.length - a.stores.length);
        setCities(list);
      } catch {
        setCities([]);
      }
    })();
  }, []);

  if (cities.length === 0) return null;

  return (
    <section id="where-to-buy" data-testid="where-to-buy" className="py-24 sm:py-32">
      <div className="mx-auto max-w-6xl px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-14 max-w-2xl"
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-blaze mb-4">Directory</p>
          <h2 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9]">
            Where to buy <span className="text-fire">Hot Packz Grabba</span>
          </h2>
          <p className="text-muted-foreground mt-5 text-base sm:text-lg">
            Hot Packz Grabba is available at authorized tobacco and smoke-shop retailers across
            North Carolina and Connecticut. Find a store near you below.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-surface-border border border-surface-border">
          {cities.map((c) => (
            <div key={`${c.city}-${c.state}`} data-testid={`city-${c.city}`} className="bg-ink p-6">
              <h3 className="font-heading uppercase text-lg tracking-tight">
                {c.city}, {c.state}
              </h3>
              <p className="text-muted-foreground text-sm mt-2 mb-5">
                Available at authorized Hot Packz Grabba retailers in {c.city}, {stateFull(c.state)}.
              </p>
              <ul className="space-y-3">
                {c.stores.map((s) => (
                  <li key={s.id} className="flex gap-2.5 text-sm">
                    <MapPin size={15} className="text-blaze shrink-0 mt-0.5" />
                    <span>
                      <span className="text-white block leading-tight">{s.name}</span>
                      <span className="text-muted-foreground">{s.address}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <p className="text-muted-foreground/70 text-xs mt-8">
          Don't see a store near you?{" "}
          <a href="#contact" className="text-blaze underline underline-offset-4">Ask us where to find Hot Packz Grabba</a>{" "}
          or{" "}
          <a href="#preorder" className="text-blaze underline underline-offset-4">order wholesale for your shop</a>.
        </p>
      </div>
    </section>
  );
}
